/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import React, { useContext, useEffect, useState } from 'react'
import Switch from 'react-switch'
import { Col, Row, FormGroup, Input, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import { isNotNumericDecimal } from 'context/Utilities'
import { packagingInitialState } from './constants'
import PlantMilkProduct from 'models/PlantMilkProduct'

const PlantMilkNutrition = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const methods = useFormContext()
  const { setSwitch, onChange, asterisk } = useFormUtils()

  useEffect(() => {
    setScrollToTop(true)
  }, [])

  useEffect(() => {
    if (current?.category === packagingInitialState.category) {
      const {
        category,
        subCategory,
        Sugar,
        SugarCertification,
        Calcium,
        CalciumCertification,
        Protein,
        ProteinCertification,
        VitaminA,
        VitaminACertification,
        VitaminD,
        VitaminDCertification,
        VitaminB12,
        VitaminB12Certification,
        Magnesium,
        MagnesiumCertification,
        Phosphorus,
        PhosphorusCertification,
        Potassium,
        PotassiumCertification,
        Riboflavin,
        RiboflavinCertification,
      } = current

      setPackaging({
        category,
        subCategory,
        Sugar,
        SugarCertification: SugarCertification ?? false,
        Calcium,
        CalciumCertification: CalciumCertification ?? false,
        Protein,
        ProteinCertification: ProteinCertification ?? false,
        VitaminA,
        VitaminACertification: VitaminACertification ?? false,
        VitaminD,
        VitaminDCertification: VitaminDCertification ?? false,
        VitaminB12,
        VitaminB12Certification: VitaminB12Certification ?? false,
        Magnesium,
        MagnesiumCertification: MagnesiumCertification ?? false,
        Phosphorus,
        PhosphorusCertification: PhosphorusCertification ?? false,
        Potassium,
        PotassiumCertification: PotassiumCertification ?? false,
        Riboflavin,
        RiboflavinCertification: RiboflavinCertification ?? false,
      })
    } else if (!updating) {
      setCurrent(PlantMilkProduct)
    }
  }, [current])

  const renderField = (
    name,
    label,
    maxLength,
    min,
    placeholder,
    pattern,
    message,
    max,
    maxMessage
  ) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Input}
        type="number"
        step="0.01"
        name={name}
        placeholder={placeholder}
        control={methods.control}
        rules={{
          required: `⚠ Please enter ${label}.`,
          maxLength: {
            value: maxLength,
            message: `⚠ ${label} exceeds ${maxLength} characters.`,
          },
          min: { value: min, message: message },
          max: max !== null ? { value: max, message: maxMessage } : undefined,
          pattern: {
            value: pattern,
            message: '⚠ Maximum two decimal places allowed.',
          },
        }}
        onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
        onChange={([event]) => {
          onChange(event)
          return event
        }}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  const renderSwitch = (name, label, errorMessage, defaultVal) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Switch}
        name={name}
        id={name}
        control={methods.control}
        defaultValue={defaultVal}
        onChange={([checked, event, id]) => {
          setSwitch(checked, event, id)
          return checked
        }}
        rules={{ validate: value => value === true }}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        message={errorMessage}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  return (
    <>
      {[
        [
          'Sugar',
          'Added Sugar (g) per 8 ounces',
          3,
          0,
          'Max 10g added sugar per 8 ounces',
          '⚠ Added Sugar (g) per 8 ounces exceeds the 10g maximum allowed.',
          'I certify that this product contains no more than 10g added sugar per 8 ounces.',
          '⚠ You must certify that this product contains no more than the maximum 10g total added sugar allowed per 8 ounces.',
          packaging.SugarCertification,
          10,
          '⚠ Added Sugar (g) per 8 ounces exceeds the 10g maximum allowed.',
        ],
        [
          'Calcium',
          'Calcium (mg) per 8 ounces',
          6,
          276,
          'Min 276mg calcium per 8 ounces',
          '⚠ Calcium (mg) per 8 ounces is below the required 276mg minimum.',
          'I certify that this product contains at least 276mg calcium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 276mg total calcium per 8 ounces.',
          packaging.CalciumCertification,
        ],
        [
          'Protein',
          'Protein (g) per 8 ounces',
          4,
          8,
          'Min 8g protein per 8 ounces',
          '⚠ Protein (g) per 8 ounces is below the required 8g minimum.',
          'I certify that this product contains at least 8g protein per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 8g total protein allowed per 8 ounces.',
          packaging.ProteinCertification,
        ],
        [
          'VitaminA',
          'Vitamin A (mcg) per 8 ounces',
          5,
          150,
          'Min 150mcg vitamin A per 8 ounces',
          '⚠ Vitamin A (mcg) per 8 ounces is below the required 150mcg minimum.',
          'I certify that this product contains at least 150mcg vitamin A per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 150mcg total vitamin A per 8 ounces.',
          packaging.VitaminACertification,
        ],
        [
          'VitaminD',
          'Vitamin D (mcg) per 8 ounces',
          4,
          2.5,
          'Min 2.5 mcg vitamin D per 8 ounces',
          '⚠ Vitamin D (mcg) per 8 ounces is below the required 2.5mcg minimum.',
          'I certify that this product contains at least 2.5mcg vitamin D per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 2.5mcg total vitamin D per 8 ounces.',
          packaging.VitaminDCertification,
        ],
        [
          'VitaminB12',
          'Vitamin B12 (mcg) per 8 ounces',
          3,
          1.1,
          'Min 1.1 mcg vitamin B12 per 8 ounces',
          '⚠ Vitamin B12 (mcg) per 8 ounces is below the required 1.1mcg minimum.',
          'I certify that this product contains at least 1.1mcg vitamin B12 per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 1.1mcg total vitamin B12 per 8 ounces.',
          packaging.VitaminB12Certification,
        ],
        [
          'Magnesium',
          'Magnesium (mg) per 8 ounces',
          5,
          24,
          'Min 24mg magnesium per 8 ounces',
          '⚠ Magnesium (mg) per 8 ounces is below the required 24mg minimum.',
          'I certify that this product contains at least 24mg magnesium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 24mg total magnesium per 8 ounces.',
          packaging.MagnesiumCertification,
        ],
        [
          'Phosphorus',
          'Phosphorus (mg) per 8 ounces',
          5,
          222,
          'Min 222mg phosphorus per 8 ounces',
          '⚠ Phosphorus (mg) per 8 ounces is below the required 222mg minimum.',
          'I certify that this product contains at least 222mg phosphorus per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 222mg total phosphorus per 8 ounces.',
          packaging.PhosphorusCertification,
        ],
        [
          'Potassium',
          'Potassium (mg) per 8 ounces',
          5,
          349,
          'Min 349mg potassium per 8 ounces',
          '⚠ Potassium (mg) per 8 ounces is below the required 349mg minimum.',
          'I certify that this product contains at least 349mg potassium per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 349mg total potassium per 8 ounces.',
          packaging.PotassiumCertification,
        ],
        [
          'Riboflavin',
          'Riboflavin (mg) per 8 ounces',
          5,
          0.44,
          'Min 0.44mg riboflavin per 8 ounces',
          '⚠ Riboflavin (mg) per 8 ounces is below the required 0.44mg minimum.',
          'I certify that this product contains at least 0.44mg riboflavin per 8 ounces.',
          '⚠ You must certify that this product contains no less than the required minimum 0.44mg total riboflavin per 8 ounces.',
          packaging.RiboflavinCertification,
        ],
      ].map(
        ([
          field,
          label,
          maxLength,
          min,
          placeholder,
          error,
          certStatement,
          certError,
          cert,
          max,
          maxMessage,
        ]) => (
          <Row key={field}>
            <Col xs={6}>
              {renderField(
                field,
                label,
                maxLength,
                min,
                placeholder,
                /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
                error,
                max,
                maxMessage
              )}
            </Col>
            <Col xs={6}>
              {renderSwitch(
                `${field}Certification`,
                certStatement,
                certError,
                cert
              )}
            </Col>
          </Row>
        )
      )}
    </>
  )
}

export default PlantMilkNutrition
